/**
 * Sentry browser client wrapper using the official tree-shaking surface
 * (`BrowserClient` directly, not `init`).
 *
 * Constructed once at boot via `initSentry`. Pulling in `BrowserClient`,
 * `defaultStackParser`, and `makeFetchTransport` directly skips Sentry's
 * auto-loaded integrations (which would balloon the bundle); we ship only basic
 * exception capture. `captureException` no-ops when no DSN was supplied so
 * dev/test environments don't need a real Sentry project. See
 * https://docs.sentry.io/platforms/javascript/configuration/tree-shaking/.
 */

import { setLocaleErrorReporter } from '@repo/shared/locale';
import { BrowserClient, defaultStackParser, getCurrentScope, makeFetchTransport } from '@sentry/browser';

let client: BrowserClient | null = null;

export function initSentry(options: { dsn?: string; environment?: string; release?: string; debug?: boolean }) {
    if (!options.dsn) return;

    client = new BrowserClient({
        dsn: options.dsn,
        environment: options.environment,
        release: options.release,
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        // Minimal integrations - none needed for basic error capture
        integrations: [],
        debug: options.debug,
    });

    getCurrentScope().setClient(client);
    client.init();

    // Route locale-parse warnings into Sentry. Same rationale as server-side
    // (server/instrument.ts) — partner integrations sending stale legacy values
    // become a visible signal.
    setLocaleErrorReporter((s) => {
        client?.captureMessage(`Unknown locale string: "${s}"`, 'warning');
    });
}

export function captureException(error: unknown): void {
    if (!client) return;
    client.captureException(error);
}
